// Local Imports
import { Requests } from "./requests";
import * as msgs from "./messages";
import { exhaust } from "./exhaust";

// # Message Platform
//
// Base class for stuff that serves as the `Platform` in our messaging system.
//
export abstract class MessageServer {
  //
  // ## Abstract Methods to Override
  //

  // Send message `msg` to the editor/ client
  abstract sendMessage: (msg: msgs.ToEditor) => unknown;

  // Handle an `EditorUp` message from the client.
  // Generally sends the initial main content.
  abstract handleEditorUp: (msg: msgs.EditorUp) => unknown;

  //
  // ## Other Default-Implemented Methods
  //

  // Overrideable logging methods
  log = (body: unknown) => console.log(body);
  warn = (body: unknown) => console.warn(body);
  error = (body: unknown) => console.error(body);

  //
  // ## Methods one could override, but probably shouldn't.
  // They're largely the point of this class.
  //

  // Handles messages from the client
  handleMessage = (msg: msgs.ToPlatform) => {
    const { kind } = msg;
    switch (kind) {
      case msgs.Kind.EditorUp:
        return this.handleEditorUp(msg);
      case msgs.Kind.ContentRequest:
        return this.handleContentRequest(msg);
      case msgs.Kind.ContentResponse:
        return this.handleMainContentResponse(msg);
      case msgs.Kind.UpdateMainContentResponse:
        return this.handleUpdateMainContentResponse(msg);
      case msgs.Kind.RefreshMainContentResponse:
        return this.handleRefreshMainContentResponse(msg);
      case msgs.Kind.MainContent:
        return this.handleMainContent(msg);
      case msgs.Kind.Log:
        return this.log(msg);
      case msgs.Kind.Warning:
        return this.warn(msg);
      case msgs.Kind.Error:
        return this.error(msg);
      case msgs.Kind.PromptRequest:
        return this.handlePromptRequest(msg);
      case msgs.Kind.GetPdkRequest:
        return this.handleGetPdk(msg);
      case msgs.Kind.GetStackRequest:
        return this.handleGetStack(msg);
      case msgs.Kind.UIChange:
        return this.handleUIChange(msg);
      case msgs.Kind.EditInstanceParameters:
        return this.handleEditInstanceParameters(msg);
      case msgs.Kind.AddInstanceAtPosition:
        return this.handleAddInstanceAtPosition(msg);
      case msgs.Kind.AddPinAtPosition:
        return this.handleAddPinAtPosition(msg);
      case msgs.Kind.RenameInstance:
        return this.handleRenameInstance(msg);
      case msgs.Kind.RenamePin:
        return this.handleRenamePin(msg);
      case msgs.Kind.GenerateFromSourceRequest:
        return this.handleGenerateFromSourceRequest(msg);
      case msgs.Kind.OpenInstanceTarget:
        return this.handleOpenInstanceTarget(msg);
      case msgs.Kind.ViewInDepot:
        return this.handleViewInDepot(msg);
      case msgs.Kind.ToggleParasitics:
        return this.handleToggleParasitics(msg);
      case msgs.Kind.GetParasiticsSummary:
        return this.handleGetParasiticsSummary(msg);
      case msgs.Kind.CaptureCanvasResponse:
        return this.handleCaptureCanvasResponse(msg);
      default:
        exhaust(kind);
    }
  };

  // Pending requests to update the main content.
  // Generally there is one, right at the beginning. And then maybe no more.
  updateMainContentRequests = new Requests<
    msgs.UpdateMainContentRequest, // <ContentType>
    msgs.UpdateMainContentResponse
  >(this);
  getMainContentRequests = new Requests<
    msgs.GetMainContentRequest,
    msgs.ContentResponse // <ContentType>
  >(this);
  refreshMainContentRequests = new Requests<
    msgs.RefreshMainContentRequest,
    msgs.RefreshMainContentResponse
  >(this);

  // Handle a `LoadedMainContent` message from the client
  // Base class does nothing; we expect most subclasses will do the same.
  handleUpdateMainContentResponse = (
    msg: msgs.UpdateMainContentResponse
  ): unknown => {
    return this.updateMainContentRequests.respond(msg);
  };

  // Handle a `RefreshMainContentResponse` message from the client
  handleRefreshMainContentResponse = (
    msg: msgs.RefreshMainContentResponse
  ): unknown => {
    return this.refreshMainContentRequests.respond(msg);
  };

  // Handle a `MainContent` message from the client
  handleMainContentResponse = (msg: msgs.ContentResponse): unknown => {
    // ContentResponse<ContentType>
    return this.getMainContentRequests.respond(msg);
  };

  // Default implementations which... probably dont work.
  handleMainContent = async (_req: msgs.MainContent) => {};
  handlePromptRequest = async (_req: msgs.PromptRequest) => {};
  handleContentRequest = async (_req: msgs.ContentRequest) => {};
  handleUIChange = async (_req: msgs.UIChange) => {};
  handleEditInstanceParameters = async (
    _req: msgs.EditInstanceParameters
  ) => {};
  handleAddInstanceAtPosition = async (_req: msgs.AddInstanceAtPosition) => {};
  handleAddPinAtPosition = async (_req: msgs.AddPinAtPosition) => {};
  handleRenameInstance = async (_req: msgs.RenameInstance) => {};
  handleRenamePin = async (_req: msgs.RenamePin) => {};

  handleGenerateFromSourceRequest = async (
    _req: msgs.GenerateFromSourceRequest
  ) => {};
  handleOpenInstanceTarget = async (_req: msgs.OpenInstanceTarget) => {};
  handleViewInDepot = async (_req: msgs.ViewInDepot) => {};
  handleToggleParasitics = async (_req: msgs.ToggleParasitics) => {};
  handleGetParasiticsSummary = async (_req: msgs.GetParasiticsSummary) => {};
  handleCaptureCanvasResponse = async (_req: msgs.CaptureCanvasResponse) => {};

  // Default implementations for Depot API message types
  handleGetPdk = async (_req: msgs.GetPdkRequest) => {};
  handleGetStack = async (_req: msgs.GetStackRequest) => {};
}

export type MessageError = {
  why: "timeout" | "couldnt-send-main-content" | "not-implemented" | "decode";
};
